import React, { useEffect, useState } from 'react';
import { Box, Paper, Checkbox, FormControlLabel, Button, useTheme, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateOrgStructureItem } from '../../hooks/orgStructureItems/useCreateOrgStructureItem';
import { useUpdateOrgStructureItem } from '../../hooks/orgStructureItems/useUpdateOrgStructureItem';
import { useGetOrgStructureItemsTree } from '../../hooks/orgStructureItems/useGetOrgStructureItemsTree';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const FormAddEditOrgStructureItem = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { createOrgStructureItem, isPending: isCreating } = useCreateOrgStructureItem();
    const { editOrgStructureItem, isUpdate } = useUpdateOrgStructureItem();
    const { isLoading: itemsLoading, treeItems: orgStructureItems } = useGetOrgStructureItemsTree();

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        parent_id: '',
        code: '',
        name: '',
        display_order: 0,
        allows_multiple: false,
        is_active: true,
    });

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            parent_id: currentData.parent_id || '',
            code: currentData.code || '',
            name: currentData.name || '',
            display_order: currentData.display_order ?? 0,
            allows_multiple: currentData.allows_multiple ?? false,
            is_active: currentData.is_active ?? true,
        });
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setForm((prev) => ({ ...prev, [name]: checked }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            parent_id: form.parent_id ? Number(form.parent_id) : null,
            code: form.code || null,
            name: form.name,
            display_order: Number(form.display_order) || 0,
            allows_multiple: !!form.allows_multiple,
            is_active: !!form.is_active,
        };

        if (isEdit && currentData?.id) {
            editOrgStructureItem({ req: payload, id: currentData.id });
        } else {
            createOrgStructureItem(payload);
        }
    };

    if (itemsLoading) return <LoadingPage />;

    const parentOptions = (orgStructureItems || [])
        .filter((item) => !(isEdit && currentData?.id && item.id === currentData.id))
        .map((item) => ({ id: item.id, name: item.name }));

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'العنصر الأب (اختياري)' : 'Parent Item (optional)'}
                            type="select" name="parent_id" value={form.parent_id}
                            onChange={(e) => setForm((prev) => ({ ...prev, parent_id: e.target.value }))}
                            options={[{ id: '', name: isArabic ? 'بدون عنصر أب' : 'No parent' }, ...parentOptions]}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'كود العنصر' : 'Item Code'}
                            placeholder={isArabic ? 'مثال: OSI-01' : 'e.g. OSI-01'}
                            type="text" name="code" value={form.code}
                            onChange={handleChange} required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'اسم العنصر' : 'Item Name'}
                            placeholder={isArabic ? 'مثال: مدير المدرسة' : 'e.g. School Director'}
                            type="text" name="name" value={form.name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'ترتيب العرض' : 'Display Order'}
                            placeholder="0"
                            type="number" name="display_order" value={form.display_order}
                            onChange={handleChange} required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: { xs: 1, md: 1 } }}>
                            <FormControlLabel
                                control={<Checkbox checked={form.allows_multiple} name="allows_multiple" onChange={handleChange} size="small" color="info" />}
                                label={isArabic ? 'يسمح بتعدد التعيينات' : 'Allows multiple assignments'}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={form.is_active} name="is_active" onChange={handleChange} size="small" />}
                                label={isArabic ? 'مفعّل' : 'Active'}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit}>
                        {loadingSubmit ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : isEdit ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'حفظ' : 'Save')}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditOrgStructureItem;
